import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { scanBarcode } from '@/lib/api/inventory.js';
import {
  acceptAllVariances,
  approveCycleCount,
  getCycleCount,
  patchCycleCountLine,
  reviewCycleCountLine,
  submitCycleCount,
  type CycleCountDetail,
} from '@/lib/api/inventory-counts.js';
import { formatCurrency, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function CycleCountDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const scanRef = useRef<HTMLInputElement>(null);
  const [scan, setScan] = useState('');
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const q = useQuery({ queryKey: ['cycle-count', id], queryFn: () => getCycleCount(id), enabled: Boolean(id) });

  const grouped = useMemo(() => {
    const lines = q.data?.lines ?? [];
    const m = new Map<string, typeof lines>();
    for (const line of lines) {
      const bin = line.binLocation || '—';
      if (!m.has(bin)) m.set(bin, []);
      m.get(bin)!.push(line);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [q.data?.lines]);

  const countQueryKey = ['cycle-count', id] as const;

  const lineMut = useMutation({
    mutationFn: (vars: { lineId: string; countedQty: number; notes?: string; silent?: boolean }) =>
      patchCycleCountLine(id, vars.lineId, { countedQty: vars.countedQty, notes: vars.notes }),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: countQueryKey });
      if (!vars.silent) toastSuccess('Line saved');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const submitMut = useMutation({
    mutationFn: () => submitCycleCount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycle-count', id] });
      queryClient.invalidateQueries({ queryKey: ['cycle-counts'] });
      toastSuccess('Count submitted for approval');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const acceptAllMut = useMutation({
    mutationFn: () => acceptAllVariances(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycle-count', id] });
      toastSuccess('Variances accepted');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const approveMut = useMutation({
    mutationFn: () => approveCycleCount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycle-count', id] });
      queryClient.invalidateQueries({ queryKey: ['cycle-counts'] });
      toastSuccess('Count approved — adjustments posted');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  if (q.isLoading || !q.data) return <div className="text-slate-600">Loading…</div>;

  const d = q.data;
  const blind = d.blindCount && d.status === 'in_progress';

  return (
    <div className="space-y-6">
      <Link to="/inventory/counts" className="text-sm font-semibold text-primary">
        ← Cycle counts
      </Link>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-bold text-slate-950">Count {d.id.slice(0, 8)}…</h1>
        <p className="text-sm text-slate-600">
          {prettyLabel(d.status)} · {prettyLabel(d.countType)}
        </p>
        <div className="mt-3">
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full bg-primary transition-all"
              style={{
                width: `${d.progress.total ? (100 * d.progress.counted) / d.progress.total : 0}%`,
              }}
            />
          </div>
          <p className="mt-1 text-xs text-slate-600">
            {d.progress.counted} of {d.progress.total} items counted
          </p>
        </div>

        {d.status === 'in_progress' ? (
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700">Scan barcode</label>
            <input
              ref={scanRef}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 font-mono text-sm"
              placeholder="Scan item barcode — each scan adds 1 to counted qty…"
              value={scan}
              onChange={(e) => setScan(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const code = scan.trim();
                if (!code || !q.data?.lines.length) return;
                void (async () => {
                  const detail =
                    queryClient.getQueryData<CycleCountDetail>(countQueryKey) ?? q.data ?? undefined;
                  if (!detail) return;

                  const norm = (b: string | null | undefined) => (b ?? '').trim();
                  let match = detail.lines.find((l) => norm(l.barcodeInternal) === code);

                  if (!match) {
                    try {
                      const hit = await scanBarcode(code);
                      if (hit.type === 'item' && hit.data?.id) {
                        const itemId = String(hit.data.id);
                        match = detail.lines.find((l) => l.itemId === itemId);
                      } else if (hit.type === 'serial' && hit.data?.itemId) {
                        const itemId = String(hit.data.itemId);
                        match = detail.lines.find((l) => l.itemId === itemId);
                      }
                    } catch {
                      // scanBarcode 404 or network — treat as no match
                    }
                  }

                  setScan('');

                  if (!match) {
                    toastError('No matching item in this count for that barcode');
                    scanRef.current?.focus();
                    return;
                  }

                  const fresh =
                    queryClient.getQueryData<CycleCountDetail>(countQueryKey)?.lines.find((l) => l.id === match!.id) ??
                    match;
                  const next = (Number(fresh.countedQty ?? 0) || 0) + 1;

                  lineMut.mutate(
                    {
                      lineId: match.id,
                      countedQty: next,
                      notes: fresh.notes ?? undefined,
                      silent: true,
                    },
                    {
                      onSuccess: () => {
                        setHighlightId(match!.id);
                        const el = document.getElementById(`count-line-${match!.id}`);
                        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        window.setTimeout(() => setHighlightId(null), 2000);
                        scanRef.current?.focus();
                      },
                    },
                  );
                })();
              }}
            />
          </div>
        ) : null}

        <div className="mt-6 space-y-6">
          {grouped.map(([bin, lines]) => (
            <div key={bin} className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Bin: {bin}</div>
              <div className="mt-3 space-y-4">
                {lines.map((line) => (
                  <CountLineRow
                    key={line.id}
                    line={line}
                    blind={Boolean(blind)}
                    disabled={d.status !== 'in_progress'}
                    highlight={highlightId === line.id}
                    onSave={(countedQty, notes) =>
                      lineMut.mutate({ lineId: line.id, countedQty, notes, silent: false })
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {d.status === 'in_progress' ? (
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
              disabled={submitMut.isPending}
              onClick={() => submitMut.mutate()}
            >
              Submit count
            </button>
          </div>
        ) : null}

        {d.status === 'pending_approval' ? (
          <div className="mt-6 space-y-4 border-t border-slate-200 pt-4">
            <h3 className="font-bold text-slate-900">Variance review</h3>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
                onClick={() => acceptAllMut.mutate()}
                disabled={acceptAllMut.isPending}
              >
                Accept all variances
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
                onClick={() => approveMut.mutate()}
                disabled={approveMut.isPending}
              >
                Approve count
              </button>
            </div>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500">
                  <th className="py-2">Item</th>
                  <th className="py-2">Expected</th>
                  <th className="py-2">Counted</th>
                  <th className="py-2">Variance</th>
                  <th className="py-2">Value</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {d.lines
                  .filter((l) => Number(l.variance ?? 0) !== 0)
                  .map((line) => (
                    <tr key={line.id}>
                      <td className="py-2 font-medium">{line.itemName}</td>
                      <td className="py-2">{blind ? '—' : line.expectedQty}</td>
                      <td className="py-2">{line.countedQty}</td>
                      <td className={Number(line.variance) > 0 ? 'text-green-700' : 'text-red-700'}>
                        {line.variance}
                      </td>
                      <td className="py-2">{formatCurrency(line.varianceCost)}</td>
                      <td className="py-2">
                        <button
                          type="button"
                          className="text-xs font-semibold text-primary"
                          onClick={() =>
                            reviewCycleCountLine(id, line.id, 'accept').then(() => {
                              queryClient.invalidateQueries({ queryKey: ['cycle-count', id] });
                            })
                          }
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          className="ml-2 text-xs font-semibold text-slate-600"
                          onClick={() =>
                            reviewCycleCountLine(id, line.id, 'reject').then(() => {
                              queryClient.invalidateQueries({ queryKey: ['cycle-count', id] });
                            })
                          }
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          className="ml-2 text-xs font-semibold text-amber-800"
                          onClick={() =>
                            reviewCycleCountLine(id, line.id, 'recount').then(() => {
                              queryClient.invalidateQueries({ queryKey: ['cycle-count', id] });
                            })
                          }
                        >
                          Recount
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function CountLineRow({
  line,
  blind,
  disabled,
  highlight,
  onSave,
}: {
  line: {
    id: string;
    itemName: string;
    barcodeInternal: string | null;
    expectedQty: string | null;
    countedQty: string | null;
    notes: string | null;
  };
  blind: boolean;
  disabled: boolean;
  highlight: boolean;
  onSave: (countedQty: number, notes?: string) => void;
}) {
  const [counted, setCounted] = useState(line.countedQty ?? '');
  const [notes, setNotes] = useState(line.notes ?? '');

  useEffect(() => {
    setCounted(line.countedQty ?? '');
    setNotes(line.notes ?? '');
  }, [line.countedQty, line.notes]);

  return (
    <div
      id={`count-line-${line.id}`}
      className={[
        'rounded-xl border border-slate-200 bg-white p-3',
        highlight ? 'ring-2 ring-primary' : '',
      ].join(' ')}
    >
      <div className="font-semibold text-slate-900">{line.itemName}</div>
      <div className="mt-1 font-mono text-xs text-slate-500">{line.barcodeInternal ?? '—'}</div>
      <div className="mt-2 text-sm text-slate-600">
        Expected: {blind ? '—' : (line.expectedQty ?? '0')}
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <label className="text-xs font-medium text-slate-700">
          Counted
          <input
            type="number"
            min="0"
            step="1"
            disabled={disabled}
            className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1"
            value={counted}
            onChange={(e) => setCounted(e.target.value)}
          />
        </label>
        <label className="text-xs font-medium text-slate-700">
          Notes
          <input
            disabled={disabled}
            className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
      </div>
      {!disabled ? (
        <button
          type="button"
          className="mt-2 text-xs font-semibold text-primary"
          onClick={() => onSave(Number(counted), notes || undefined)}
        >
          Save line
        </button>
      ) : null}
    </div>
  );
}
